import { useEffect, useRef, useState } from 'react'
import { cx } from './primitives'

export interface ActionMenuItem {
  label: string
  onSelect: () => void
  /**
   * Destructive. Drawn in the critical colour, below a divider, after everything else -
   * whatever order the caller wrote them in.
   */
  danger?: boolean
  disabled?: boolean
  /** Under the label, for a verb whose consequence is not obvious from two words. */
  hint?: string
}

/**
 * The rare and the dangerous, out of the row.
 *
 * <p>
 * A header can only carry so many controls before it is scanned rather than read. This is
 * where everything that is used on a small fraction of records goes, so the row keeps one
 * primary action, the state's frequent verb, and nothing else competing with them.
 * </p>
 *
 * Destructive items sort to the bottom under a divider rather than sitting where the caller
 * wrote them: a menu whose last entry is always the one that destroys something is a menu
 * where the pointer never crosses that entry on its way to anything else.
 *
 * <p>
 * With <code>text</code> it is a button that says what it is for and opens onto the answers:
 * one verb with more than one way to finish it. That is still one control in the row, which
 * two buttons side by side would not be.
 * </p>
 */
export function ActionMenu({
  items,
  label = 'More actions',
  text,
  align = 'right',
}: {
  items: ActionMenuItem[]
  /** What the control is called when it shows no words of its own. */
  label?: string
  /** Words on the button, in place of the three dots. They are then its name as well. */
  text?: string
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return

      setOpen(false)

      // Back to the button that opened it. Escape that drops focus to the document leaves
      // a keyboard user at the top of the page with no idea where they were.
      trigger.current?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Nothing to show, so no control. A permission set that empties the menu should not leave
  // a button that opens onto an empty box.
  if (items.length === 0) return null

  const { ordered, dividerAt } = arrange(items)

  return (
    <div ref={container} className="relative">
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        // Words on the button are its name already. A second one over the top of them is
        // what a screen reader would announce instead.
        aria-label={text ? undefined : label}
        title={text ? undefined : label}
        onClick={() => setOpen((was) => !was)}
        className={cx(
          'inline-flex h-10 items-center justify-center rounded-md border text-sm font-medium',
          'border-line bg-surface transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          // Worded, it is drawn as the buttons either side of it are, because it is one of
          // them. The dots are quieter on purpose: they are where the rare things go.
          text ? 'gap-2 px-4 text-ink hover:bg-surface-2' : 'px-3 text-ink-2 hover:bg-sunken hover:text-ink',
          open && (text ? 'bg-surface-2' : 'bg-sunken text-ink'),
        )}
      >
        {text ? (
          <>
            {text}
            <Chevron />
          </>
        ) : (
          <Ellipsis />
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={text ?? label}
          className={cx(
            'absolute z-30 mt-1 w-60 rounded-lg border border-line bg-surface p-1 shadow-float',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {ordered.map((item, index) => (
            <div key={item.label}>
              {index === dividerAt && <div className="my-1 h-px bg-line-soft" role="separator" />}

              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false)
                  item.onSelect()
                }}
                className={cx(
                  'w-full rounded px-2.5 py-1.5 text-left text-[13px] transition-colors',
                  'disabled:pointer-events-none disabled:opacity-50',
                  item.danger
                    ? 'text-crit hover:bg-crit-wash'
                    : 'text-ink-2 hover:bg-sunken hover:text-ink',
                )}
              >
                {item.label}
                {item.hint && <span className="block text-[11.5px] text-muted">{item.hint}</span>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The order items are drawn in, and where the divider goes.
 *
 * <p>
 * Destructive items sink to the bottom whatever order the caller wrote them in, so the
 * pointer never crosses one on its way to something harmless. The divider is only drawn
 * when there is something above it to divide from - a menu that opens with a rule across
 * its top reads as a menu missing its first item.
 * </p>
 *
 * Exported so the ordering can be asserted without rendering anything.
 */
export function arrange(items: ActionMenuItem[]): {
  ordered: ActionMenuItem[]
  dividerAt: number | null
} {
  const safe = items.filter((item) => !item.danger)
  const danger = items.filter((item) => item.danger)

  return {
    ordered: [...safe, ...danger],
    dividerAt: safe.length > 0 && danger.length > 0 ? safe.length : null,
  }
}

/** The same arrow a dropdown carries, so a button that opens reads as one that does. */
function Chevron() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="-mr-1 size-4 text-muted">
      <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Ellipsis() {
  return (
    <svg aria-hidden viewBox="0 0 16 4" className="h-1 w-4 fill-current">
      <circle cx="2" cy="2" r="1.6" />
      <circle cx="8" cy="2" r="1.6" />
      <circle cx="14" cy="2" r="1.6" />
    </svg>
  )
}
